import { useTranslation } from "react-i18next";
import { Row, Col, Card, CardBody } from "react-bootstrap";
import { useEffect, useState } from "react";
import AccountabilityEndpoints from "@serie3/mymediaplanner/Modules/Accountability/endpoints";
import crudApi from "@serie3/common/api/crudApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine } from "@fortawesome/free-solid-svg-icons";
import NewModal from "../../../shared/Modals/NewModal";
import DonutChart from "../Charts/DonutChart";

const getMonthLabelFromValid = (valid) => {
  if (!valid) return "";
  const [yearTime, month] = valid.split("-");
  const [year] = yearTime.split(", ");
  try {
    const d = new Date(`${year}-${month}-01`);
    return d.toLocaleString("default", { month: "long", year: "numeric" });
  } catch {
    return valid;
  }
};

const buildChartData = (a) => {
  const posts = {
    Reached: a.reached_post_amount || 0,
    Planned: Math.max(0, a.post_amount - (a.reached_post_amount || 0)),
  };
  const requests = {
    Reached: a.reached_request_amount || 0,
    Planned: Math.max(0, a.request_amount - (a.reached_request_amount || 0)),
  };
  const icebreaker = {
    Reached: a.reached_messages_amount || 0,
    Planned: Math.max(0, a.messages_amount - (a.reached_messages_amount || 0)),
  };
  return { posts, requests, icebreaker };
};

const AccountabilityHistory = ({ props }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetAccountability = async () => {
      await crudApi.getAll(
        AccountabilityEndpoints.index,
        setData,
        AccountabilityEndpoints.dataField
      );
      setLoading(false);
    };
    fetAccountability();
  }, []);

  const chartData = selected ? buildChartData(selected) : null;

  return (
    <>
      {selected && (
        <NewModal
          show={showModal}
          setShow={setShowModal}
          title={`${selected.platform} — ${getMonthLabelFromValid(selected.valid)}`}
          onExit={() => setSelected(null)}
          size="xl"
        >
          <Row className="align-items-center">
            <Col md={4}>
              <DonutChart
                chartLabels={Object.keys(chartData.posts)}
                series={Object.values(chartData.posts)}
                title={t("Backend.Accountability.Labels.Posts")}
                type="donut"
                noCard
              />
            </Col>
            <Col md={4}>
              <DonutChart
                chartLabels={Object.keys(chartData.requests)}
                series={Object.values(chartData.requests)}
                title={t("Backend.Accountability.Labels.Requests")}
                type="donut"
                noCard
              />
            </Col>
            <Col md={4}>
              <DonutChart
                chartLabels={Object.keys(chartData.icebreaker)}
                series={Object.values(chartData.icebreaker)}
                title={t("Backend.Accountability.Labels.Icebreaker")}
                type="donut"
                noCard
              />
            </Col>
          </Row>
        </NewModal>
      )}

      <h4 className="page-section-header">
        <span className="page-section-header-icon" style={{ background: "rgba(3,142,220,0.12)" }}>
          <FontAwesomeIcon icon={faChartLine} style={{ color: "#038edc" }} />
        </span>
        {t("Backend.Accountability.HistoryTitle")}
      </h4>
      <Card>
        <CardBody>
          <Row>
            <Col>
              {loading ? (
                <div style={{ padding: "8px 0" }}>
                  {[1, 2, 3].map((i) => (
                    <div key={i} style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: "1px solid #f3f4f6" }}>
                      <div className="skeleton" style={{ width: 80, height: 14 }} />
                      <div className="skeleton" style={{ width: 100, height: 14 }} />
                      <div className="skeleton" style={{ width: 60, height: 14 }} />
                      <div className="skeleton" style={{ width: 60, height: 14 }} />
                      <div className="skeleton" style={{ width: 60, height: 14 }} />
                    </div>
                  ))}
                </div>
              ) : (
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("Backend.Accountability.Labels.Valid")}</th>
                      <th>{t("Backend.Accountability.Labels.Platform")}</th>
                      <th>{t("Backend.Accountability.Labels.Posts")}</th>
                      <th>{t("Backend.Accountability.Labels.Requests")}</th>
                      <th>{t("Backend.Accountability.Labels.Icebreaker")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {(() => { let lastMonthKey = null; return data.map((accountability) => {
                      const [yearTime, month] = accountability.valid.split("-");
                      const [year] = yearTime.split(", ");
                      const currentDate = new Date();
                      const currentYear = currentDate.getFullYear();
                      const currentMonth = currentDate.getMonth() + 1;
                      const isPastMonth =
                        year < currentYear ||
                        (year === currentYear && month < currentMonth);
                      if (!isPastMonth) return null;
                      const monthKey = accountability.valid;
                      const showSeparator = monthKey !== lastMonthKey;
                      lastMonthKey = monthKey;
                      return (
                        <>
                          {showSeparator && (
                            <tr key={`sep-${monthKey}`} className="month-separator-row">
                              <td colSpan={6}>
                                <span className="month-separator-label">{getMonthLabelFromValid(accountability.valid)}</span>
                              </td>
                            </tr>
                          )}
                          <tr
                            key={accountability.id}
                            style={{ cursor: "pointer" }}
                            onClick={() => { setSelected(accountability); setShowModal(true); }}
                          >
                            <td style={{ color: "#9299a8", whiteSpace: "nowrap" }}>{month + "." + yearTime}</td>
                            <td style={{ fontWeight: 600, color: "#111827" }}>{accountability.platform}</td>
                            <td>
                              <span className="progress-cell">
                                <span className="reached">{accountability.reached_post_amount || 0}</span>
                                <span className="divider">/</span>
                                <span className="total">{accountability.post_amount}</span>
                              </span>
                            </td>
                            <td>
                              <span className="progress-cell">
                                <span className="reached">{accountability.reached_request_amount || 0}</span>
                                <span className="divider">/</span>
                                <span className="total">{accountability.request_amount}</span>
                              </span>
                            </td>
                            <td>
                              <span className="progress-cell">
                                <span className="reached">{accountability.reached_messages_amount || 0}</span>
                                <span className="divider">/</span>
                                <span className="total">{accountability.messages_amount}</span>
                              </span>
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelected(accountability); setShowModal(true); }}
                                style={{
                                  background: "rgba(3,142,220,0.10)",
                                  border: "none",
                                  borderRadius: 8,
                                  color: "#038edc",
                                  padding: "5px 10px",
                                  cursor: "pointer",
                                  fontSize: 13,
                                  transition: "background 0.15s ease",
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(3,142,220,0.2)"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(3,142,220,0.10)"; }}
                                title={t("Backend.Accountability.HistoryTitle")}
                              >
                                <FontAwesomeIcon icon={faChartLine} />
                              </button>
                            </td>
                          </tr>
                        </>
                      );
                    }); })()}
                  </tbody>
                </table>
              </div>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>
    </>
  );
};

export default AccountabilityHistory;
